/* Regression detection. Pure, deterministic, and deliberately simple.
 *
 * This is a set of thresholds over facts the platform already stores, not an anomaly engine. It
 * lives here rather than in a component so the rules are testable, and it takes its thresholds as
 * an argument so the day `core.ai.anomaly` or a stored per-tenant policy supplies them, the only
 * thing that changes is the caller.
 *
 * **An unscored dataset never raises a regression, and that is the rule this file most exists to
 * hold.** A dataset nobody has measured is a coverage gap. Alerting on it would put the estate's
 * largest, most permanent category into the feed and make the feed unreadable — which is precisely
 * how a real regression comes to be ignored. It is reported in the summary strip and in its own
 * saved view instead, where it belongs.
 */

import type { DatasetRow, Regression } from './types';

export interface DetectionThresholds {
  /** A fall of at least this many points is a regression. */
  readonly scoreDrop: number;
  /** Twice the drop threshold is critical rather than merely a regression. */
  readonly criticalMultiplier: number;
  /** A fall in column coverage of at least this ratio is worth reporting on its own. */
  readonly coverageDrop: number;
  /** Report a scored dataset that has not been re-evaluated in this long. */
  readonly staleHours: number;
}

export const DEFAULT_THRESHOLDS: DetectionThresholds = {
  scoreDrop: 2,
  criticalMultiplier: 2,
  coverageDrop: 0.1,
  staleHours: 24,
};

const ORDER: Readonly<Record<Regression['severity'], number>> = {
  critical: 0,
  warning: 1,
  info: 2,
};

/**
 * Every finding across the estate, worst first.
 *
 * Six detectors, and each answers a question the others cannot:
 *
 * * **score-drop** — the score fell. The headline regression.
 * * **below-target** — the score breaches a contract term. Independent of movement: a dataset that
 *   has sat below its floor for a month is not moving and is still in breach.
 * * **coverage-fell** — fewer columns are checked than were. The sharpest case is a score that
 *   *rose* while coverage fell, which is a score improving because it is checking less, and it gets
 *   its own wording rather than being folded in.
 * * **partial-evidence** — the execution behind the score did not measure everything it set out to.
 * * **model-change** — the window crosses a score-model change, so the movement is partly a
 *   reweighting. Reported as information, never as a regression: attributing a definition change to
 *   the data is the one thing `Trend.segments` exists to prevent.
 * * **stale-evaluation** — the score is real and old. Not a quality finding; a trust finding.
 */
export function detectRegressions(
  rows: readonly DatasetRow[],
  thresholds: DetectionThresholds = DEFAULT_THRESHOLDS,
): readonly Regression[] {
  const found: Regression[] = [];

  for (const row of rows) {
    /* The whole reason this guard is first: an unmeasured dataset is not a degraded one. */
    if (row.value === null) continue;

    const base = { datasetId: row.id, datasetName: row.name };
    const movement = row.movement;

    if (movement?.delta.kind === 'down') {
      const { magnitude } = movement.delta;
      if (magnitude >= thresholds.scoreDrop) {
        found.push({
          ...base,
          severity:
            magnitude >= thresholds.scoreDrop * thresholds.criticalMultiplier
              ? 'critical'
              : 'warning',
          kind: 'score-drop',
          messageKey: 'scoring.regression.scoreDrop',
          messageArgs: { points: magnitude.toFixed(1) },
          detailKey: 'scoring.regression.scoreDropDetail',
          detailArgs: { threshold: thresholds.scoreDrop.toFixed(1) },
          tab: 'change',
        });
      }
    }

    if (movement?.delta.kind === 'incomparable') {
      found.push({
        ...base,
        severity: 'info',
        kind: 'model-change',
        messageKey: 'scoring.regression.modelChange',
        messageArgs: {},
        detailKey: 'scoring.regression.modelChangeDetail',
        detailArgs: {},
        tab: 'runs',
      });
    }

    if (row.slack !== null && row.slack < 0 && row.target !== null) {
      found.push({
        ...base,
        /* The contract's own `breach_severity` wins over the size of the gap: a regulator-facing
           contract escalates a small breach where an internal one does not, and that decision was
           made by the two parties who signed it rather than by this screen. */
        severity: row.target.severity === 'info' ? 'info' : row.target.severity,
        kind: 'below-target',
        messageKey: 'scoring.regression.belowTarget',
        messageArgs: { points: Math.abs(row.slack).toFixed(1) },
        detailKey: 'scoring.regression.belowTargetDetail',
        detailArgs: { contract: row.target.contractName, target: row.target.minScore },
        tab: 'decomposition',
      });
    }

    if (movement !== undefined) {
      const { coverageNow, coverageThen } = movement;
      if (
        coverageNow !== null &&
        coverageThen !== null &&
        coverageThen - coverageNow >= thresholds.coverageDrop
      ) {
        const roseWhileNarrowing = movement.delta.kind === 'up';
        found.push({
          ...base,
          severity: 'warning',
          kind: 'coverage-fell',
          messageKey: roseWhileNarrowing
            ? 'scoring.regression.coverageFellWhileRising'
            : 'scoring.regression.coverageFell',
          messageArgs: { points: Math.round((coverageThen - coverageNow) * 100) },
          detailKey: 'scoring.regression.coverageFellDetail',
          detailArgs: {
            before: Math.round(coverageThen * 100),
            after: Math.round(coverageNow * 100),
          },
          tab: 'decomposition',
        });
      }
    }

    if (!row.evidenceComplete) {
      found.push({
        ...base,
        severity: 'warning',
        kind: 'partial-evidence',
        messageKey: 'scoring.regression.partial',
        messageArgs: {},
        detailKey: 'scoring.regression.partialDetail',
        detailArgs: {},
        tab: 'decomposition',
      });
    }

    if (row.evaluatedHoursAgo !== null && row.evaluatedHoursAgo > thresholds.staleHours) {
      found.push({
        ...base,
        severity: 'warning',
        kind: 'stale-evaluation',
        messageKey: 'scoring.regression.stale',
        messageArgs: { hours: Math.round(row.evaluatedHoursAgo) },
        detailKey: 'scoring.regression.staleDetail',
        detailArgs: { threshold: thresholds.staleHours },
        tab: 'runs',
      });
    }
  }

  /* Sorted by severity, then by dataset name so two renders of one estate never reorder the feed
     under a reader's cursor. Stable ordering matters more here than anywhere else on the page:
     these are buttons somebody is about to click. */
  return found.sort(
    (left, right) =>
      ORDER[left.severity] - ORDER[right.severity] ||
      left.datasetName.localeCompare(right.datasetName) ||
      left.kind.localeCompare(right.kind),
  );
}
